import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "./PortalDesigner.css";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { ArrowLeft, Save, Eye, Settings, Palette, Lock, MapPin, DollarSign, Share2, FileText, Plus, Trash2, Upload, QrCode, Image as ImageIcon, Camera, Download } from "lucide-react";
import HotspotPreview from "../components/HotspotPreview";
import QRScanner from "../components/QRScanner";

const PortalDesignerEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [template, setTemplate] = useState({
    name: "Nueva Plantilla",
    description: "",
    design: {
      primaryColor: "#3b82f6",
      secondaryColor: "#8b5cf6",
      backgroundColor: "#ffffff",
      textColor: "#1f2937",
      logo: "",
      headerBackgroundColor: "",
      headerTextColor: "",
      loginCardBackground: "#ffffff",
      loginCardBorder: "#e5e7eb",
      loginCardTextColor: "#1f2937",
      inputBackground: "#ffffff",
      inputBorder: "#d1d5db",
      inputTextColor: "#1f2937",
      inputPlaceholderColor: "#9ca3af",
      buttonBackground: "",
      buttonTextColor: "#ffffff",
      buttonHoverBackground: "",
      pricingCardBackground: "#ffffff",
      pricingCardBorder: "",
      pricingCardTextColor: "#1f2937",
      pricingCardTitleColor: "",
      locationCardBackground: "#ffffff",
      locationCardBorder: "#e5e7eb",
      locationCardTextColor: "#1f2937",
      iconColor: "",
      linkColor: "",
      socialIconBackground: ""
    },
    loginConfig: {
      method: "userpass",
      enabled: true,
      qrImage: "",
      socialProviders: [],
      customForm: false,
      formFields: [],
      multipleMethods: [],
      defaultMethod: "userpass",
      pinMode: "both"
    },
    popup: {
      enabled: false,
      title: "",
      message: ""
    },
    socialMedia: {
      facebook: "",
      instagram: "",
      twitter: "",
      whatsapp: ""
    },
    locations: [],
    pricing: [],
    termsAndConditions: "",
    sslConfig: {
      enabled: false,
      certificate: "",
      privateKey: ""
    },
    hotspotPages: {
      redirectUrl: "https://www.google.com",
      statusTitle: "Estado de Conexión",
      statusMessage: "Estás conectado exitosamente",
      logoutTitle: "Sesión Cerrada",
      logoutMessage: "Has cerrado sesión correctamente",
      errorTitle: "Error de Conexión",
      errorMessage: "No se pudo conectar. Verifica tus credenciales."
    }
  });

  useEffect(() => {
    if (id) {
      loadTemplate();
    }
  }, [id]);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL + "/api"}/portals/${id}`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      setTemplate(response.data);
    } catch (error) {
      console.error("Error loading template:", error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      
      if (id) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL + "/api"}/portals/${id}`, 
          template,
          { headers }
        );
        alert('✅ Portal guardado exitosamente');
      } else {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL + "/api"}/portals`, 
          template,
          { headers }
        );
        alert('✅ Portal creado exitosamente');
        navigate(`/portal-designer/editor/${response.data.id}`);
      }
    } catch (error) {
      console.error("Error saving template:", error);
      alert('❌ Error al guardar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL + "/api"}/upload/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setTemplate(prev => ({
        ...prev,
        design: { ...prev.design, logo: response.data.url }
      }));
    } catch (error) {
      console.error("Error uploading logo:", error);
    }
  };

  const handleQRUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL + "/api"}/upload/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setTemplate(prev => ({
        ...prev,
        loginConfig: { ...prev.loginConfig, qrImage: response.data.url }
      }));
    } catch (error) {
      console.error("Error uploading QR:", error);
    }
  };

  const handleQRScan = (qrData) => {
    setShowQRScanner(false);
    setTemplate(prev => ({
      ...prev,
      loginConfig: { ...prev.loginConfig, qrData: qrData }
    }));
  };

  const handleExport = async () => {
    // 🔒 Mostrar mensaje de función de paga
    alert('🔒 Esta función es de paga.\n\nContacte al administrador para activar la descarga de portales.');
  };

  const generateGenericSSL = () => {
    const genericCert = `-----BEGIN CERTIFICATE-----
MIIDXTCCAkWgAwIBAgIJAKL0UG+mRkSvMA0GCSqGSIb3DQEBCwUAMEUxCzAJBgNV
BAYTAkFVMRMwEQYDVQQIDApTb21lLVN0YXRlMSEwHwYDVQQKDBhJbnRlcm5ldCBX
aWRnaXRzIFB0eSBMdGQwHhcNMjQwMTAxMDAwMDAwWhcNMjUwMTAxMDAwMDAwWjBF
MQswCQYDVQQGEwJBVTETMBEGA1UECAwKU29tZS1TdGF0ZTEhMB8GA1UECgwYSW50
ZXJuZXQgV2lkZ2l0cyBQdHkgTHRkMIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIB
CgKCAQEAw8jK+VJXLvF5V9xGvZ7LGW7HqBcPxFJvPyN0gLx0tqZPZNJQvYJDYmVx
... (Certificado SSL genérico autofirmado)
-----END CERTIFICATE-----`;

    const genericKey = `-----BEGIN PRIVATE KEY-----
MIIEvgIBADANBgkqhkiG9w0BAQEFAASCBKgwggSkAgEAAoIBAQDDyMr5Ulcu8XlX
3Ea9nssZbseoFw/EUm8/I3SAvHS2pk9k0lC9gkNiZXFOQ8KZ7HYKvPMjL0PJ5Xhl
... (Clave privada genérica)
-----END PRIVATE KEY-----`;

    setTemplate(prev => ({
      ...prev,
      sslConfig: {
        ...prev.sslConfig,
        enabled: true,
        certificate: genericCert,
        privateKey: genericKey
      }
    }));
  };

  const addLocation = () => {
    setTemplate(prev => ({
      ...prev,
      locations: [...prev.locations, { name: "", address: "", phone: "" }]
    }));
  };

  const removeLocation = (index) => {
    setTemplate(prev => ({
      ...prev,
      locations: prev.locations.filter((_, i) => i !== index)
    }));
  };

  const updateLocation = (index, field, value) => {
    setTemplate(prev => ({
      ...prev,
      locations: prev.locations.map((loc, i) => 
        i === index ? { ...loc, [field]: value } : loc
      )
    }));
  };

  const addPricing = () => {
    setTemplate(prev => ({
      ...prev,
      pricing: [...prev.pricing, { name: "", duration: "", price: "", speed: "" }]
    }));
  };

  const removePricing = (index) => {
    setTemplate(prev => ({
      ...prev,
      pricing: prev.pricing.filter((_, i) => i !== index)
    }));
  };

  const updatePricing = (index, field, value) => {
    setTemplate(prev => ({
      ...prev,
      pricing: prev.pricing.map((price, i) => 
        i === index ? { ...price, [field]: value } : price
      )
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="editor-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="portal-designer-light min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" style={{color: '#1f2937'}}>
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-50 mb-4">
        <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/portal-designer')} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <div>
              <h1 className="text-xl font-bold" data-testid="editor-title">{template.name}</h1>
              <p className="text-sm text-gray-600" data-testid="editor-subtitle">Editor de plantilla</p>
            </div>
          </div>
          <div className="flex gap-2">
            {id && (
              <Button 
                onClick={handleExport}
                variant="outline"
                className="bg-green-50 hover:bg-green-100 border-green-200"
                data-testid="export-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar Portal
              </Button>
            )}
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              data-testid="save-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 pt-4 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Editor Panel */}
          <div className="space-y-6" data-testid="editor-panel">
            <Card className="p-6" style={{backgroundColor: '#ffffff', color: '#1f2937'}}>
              <Tabs defaultValue="general" className="w-full">
                <TabsList className="grid grid-cols-4 lg:grid-cols-9 mb-6 gap-1" data-testid="editor-tabs">
                  <TabsTrigger value="general" data-testid="general-tab" className="flex items-center justify-center px-2">
                    <Settings className="w-4 h-4" />
                    <span className="ml-1 hidden xl:inline text-xs">General</span>
                  </TabsTrigger>
                  <TabsTrigger value="design" data-testid="design-tab" className="flex items-center justify-center px-2">
                    <Palette className="w-4 h-4" />
                    <span className="ml-1 hidden xl:inline text-xs">Diseño</span>
                  </TabsTrigger>
                  <TabsTrigger value="pages" data-testid="pages-tab" className="flex items-center justify-center px-2" title="Páginas">
                    <FileText className="w-4 h-4" />
                    <span className="ml-1 hidden xl:inline text-xs">Páginas</span>
                  </TabsTrigger>
                  <TabsTrigger value="ssl" data-testid="ssl-tab" className="flex items-center justify-center px-2">
                    <Lock className="w-4 h-4" />
                    <span className="ml-1 hidden xl:inline text-xs">SSL</span>
                  </TabsTrigger>
                  <TabsTrigger value="locations" data-testid="locations-tab" className="flex items-center justify-center px-2">
                    <MapPin className="w-4 h-4" />
                    <span className="ml-1 hidden xl:inline text-xs">Ubicaciones</span>
                  </TabsTrigger>
                  <TabsTrigger value="pricing" data-testid="pricing-tab" className="flex items-center justify-center px-2">
                    <DollarSign className="w-4 h-4" />
                    <span className="ml-1 hidden xl:inline text-xs">Precios</span>
                  </TabsTrigger>
                  <TabsTrigger value="social" data-testid="social-tab" className="flex items-center justify-center px-2">
                    <Share2 className="w-4 h-4" />
                    <span className="ml-1 hidden xl:inline text-xs">Social</span>
                  </TabsTrigger>
                  <TabsTrigger value="terms" data-testid="terms-tab" className="flex items-center justify-center px-2">
                    <FileText className="w-3 h-3" />
                    <span className="ml-1 hidden xl:inline text-xs">Términos</span>
                  </TabsTrigger>
                  <TabsTrigger value="preview" className="lg:hidden flex items-center justify-center px-2" data-testid="preview-tab-mobile">
                    <Eye className="w-4 h-4" />
                  </TabsTrigger>
                </TabsList>

                {/* General Tab */}
                <TabsContent value="general" className="space-y-4" data-testid="general-content">
                  <div>
                    <Label htmlFor="name">Nombre de la Plantilla</Label>
                    <Input
                      id="name"
                      value={template.name}
                      onChange={(e) => setTemplate(prev => ({ ...prev, name: e.target.value }))}
                      data-testid="template-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Descripción</Label>
                    <Textarea
                      id="description"
                      value={template.description}
                      onChange={(e) => setTemplate(prev => ({ ...prev, description: e.target.value }))}
                      data-testid="template-description-input"
                    />
                  </div>
                  <div>
                    <Label>Método de Login</Label>
                    <Select
                      value={template.loginConfig.method}
                      onValueChange={(value) => setTemplate(prev => ({
                        ...prev,
                        loginConfig: { ...prev.loginConfig, method: value }
                      }))}
                    >
                      <SelectTrigger data-testid="login-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="userpass" data-testid="login-userpass">Usuario y Contraseña</SelectItem>
                        <SelectItem value="pin" data-testid="login-pin">PIN</SelectItem>
                        <SelectItem value="multiple" data-testid="login-multiple">Pin / User&Pass (Selector)</SelectItem>
                        <SelectItem value="qr" data-testid="login-qr">Código QR</SelectItem>
                        <SelectItem value="social" data-testid="login-social">Redes Sociales</SelectItem>
                        <SelectItem value="form" data-testid="login-form">Formulario Personalizado</SelectItem>
                        <SelectItem value="free" data-testid="login-free">Acceso Gratis</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {(template.loginConfig.method === 'pin' || template.loginConfig.multipleMethods?.includes('pin')) && (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <input
                          type="checkbox"
                          id="pin-fill-both"
                          checked={template.loginConfig.pinMode === 'both'}
                          onChange={(e) => setTemplate(prev => ({
                            ...prev,
                            loginConfig: { ...prev.loginConfig, pinMode: e.target.checked ? 'both' : 'username-only' }
                          }))}
                          className="rounded"
                          data-testid="pin-fill-both-checkbox"
                        />
                        <Label htmlFor="pin-fill-both" className="cursor-pointer text-sm font-medium text-blue-900">
                          Rellenar ambos (usuario y contraseña con el PIN)
                        </Label>
                      </div>
                      <p className="text-xs text-gray-600 pl-3">
                        {template.loginConfig.pinMode === 'both' 
                          ? '✓ El PIN se usará como usuario Y contraseña (mismo valor)'
                          : '✓ El PIN solo se usará como usuario (contraseña vacía)'
                        }
                      </p>
                    </div>
                  )}
                  {template.loginConfig.method === 'multiple' && (
                    <div className="space-y-3">
                      <Label>Métodos de Login Disponibles</Label>
                      <p className="text-sm text-gray-600">El usuario podrá elegir entre estos métodos</p>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="multiple-userpass"
                            checked={template.loginConfig.multipleMethods?.includes('userpass')}
                            onChange={(e) => {
                              const methods = template.loginConfig.multipleMethods || [];
                              setTemplate(prev => ({
                                ...prev,
                                loginConfig: {
                                  ...prev.loginConfig,
                                  multipleMethods: e.target.checked
                                    ? [...methods, 'userpass']
                                    : methods.filter(m => m !== 'userpass')
                                }
                              }));
                            }}
                            className="rounded"
                            data-testid="multiple-userpass-checkbox"
                          />
                          <Label htmlFor="multiple-userpass" className="cursor-pointer">Usuario y Contraseña</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="multiple-pin"
                            checked={template.loginConfig.multipleMethods?.includes('pin')}
                            onChange={(e) => {
                              const methods = template.loginConfig.multipleMethods || [];
                              setTemplate(prev => ({
                                ...prev,
                                loginConfig: {
                                  ...prev.loginConfig,
                                  multipleMethods: e.target.checked
                                    ? [...methods, 'pin']
                                    : methods.filter(m => m !== 'pin')
                                }
                              }));
                            }}
                            className="rounded"
                            data-testid="multiple-pin-checkbox"
                          />
                          <Label htmlFor="multiple-pin" className="cursor-pointer">PIN</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="multiple-qr"
                            checked={template.loginConfig.multipleMethods?.includes('qr')}
                            onChange={(e) => {
                              const methods = template.loginConfig.multipleMethods || [];
                              setTemplate(prev => ({
                                ...prev,
                                loginConfig: {
                                  ...prev.loginConfig,
                                  multipleMethods: e.target.checked
                                    ? [...methods, 'qr']
                                    : methods.filter(m => m !== 'qr')
                                }
                              }));
                            }}
                            className="rounded"
                            data-testid="multiple-qr-checkbox"
                          />
                          <Label htmlFor="multiple-qr" className="cursor-pointer">Código QR</Label>
                        </div>
                      </div>
                      
                      {template.loginConfig.multipleMethods && template.loginConfig.multipleMethods.length > 0 && (
                        <div>
                          <Label>Método Activo por Defecto</Label>
                          <Select
                            value={template.loginConfig.defaultMethod || "userpass"}
                            onValueChange={(value) => setTemplate(prev => ({
                              ...prev,
                              loginConfig: { ...prev.loginConfig, defaultMethod: value }
                            }))}
                          >
                            <SelectTrigger data-testid="default-method-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {template.loginConfig.multipleMethods.includes('userpass') && (
                                <SelectItem value="userpass" data-testid="default-userpass">
                                  Usuario y Contraseña
                                </SelectItem>
                              )}
                              {template.loginConfig.multipleMethods.includes('pin') && (
                                <SelectItem value="pin" data-testid="default-pin">
                                  PIN
                                </SelectItem>
                              )}
                              {template.loginConfig.multipleMethods.includes('qr') && (
                                <SelectItem value="qr" data-testid="default-qr">
                                  Código QR
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-gray-600 mt-1">
                            Este método estará seleccionado cuando el usuario abra la página
                          </p>
                        </div>
                      )}
                      
                      <Card className="p-4 bg-blue-50 border-blue-200" data-testid="multiple-login-instructions">
                        <h4 className="font-semibold text-blue-900 mb-2">Configuración en MikroTik</h4>
                        <p className="text-sm text-blue-800 mb-2">
                          Para habilitar múltiples métodos de login:
                        </p>
                        <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                          <li>IP → Hotspot → Server Profiles</li>
                          <li>En "Login By": habilita HTTP CHAP, HTTP PAP, HTTPS</li>
                          <li>Configura los métodos que quieras ofrecer</li>
                          <li>La página de login mostrará pestañas para cada método</li>
                        </ol>
                      </Card>
                    </div>
                  )}
                  {template.loginConfig.method === 'qr' && (
                    <div className="space-y-3">
                      <Label className="flex items-center gap-2">
                        <QrCode className="w-4 h-4" />
                        Código QR
                      </Label>
                      
                      {!showQRScanner ? (
                        <>
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              id="qr-upload"
                              type="file"
                              accept="image/*"
                              onChange={handleQRUpload}
                              className="hidden"
                              data-testid="qr-upload-input"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => document.getElementById('qr-upload').click()}
                              className="w-full"
                              data-testid="qr-upload-btn"
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              Subir QR
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setShowQRScanner(true)}
                              className="w-full"
                              data-testid="qr-scan-btn"
                            >
                              <Camera className="w-4 h-4 mr-2" />
                              Escanear QR
                            </Button>
                          </div>
                          {template.loginConfig.qrImage ? (
                            <div className="relative border-2 border-dashed rounded-lg p-4 flex items-center justify-center bg-gray-50" data-testid="qr-preview-container">
                              <img src={template.loginConfig.qrImage} alt="QR Code" className="h-32 object-contain" data-testid="qr-preview" />
                              <Button
                                variant="destructive"
                                size="sm"
                                className="absolute top-2 right-2"
                                onClick={() => setTemplate(prev => ({ 
                                  ...prev, 
                                  loginConfig: { ...prev.loginConfig, qrImage: "" } 
                                }))}
                                data-testid="qr-remove-btn"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="border-2 border-dashed rounded-lg p-8 text-center text-gray-400" data-testid="qr-placeholder">
                              <QrCode className="w-12 h-12 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">Sin código QR</p>
                              <p className="text-xs mt-1">Sube una imagen o escanea con tu cámara</p>
                            </div>
                          )}
                        </>
                      ) : (
                        <QRScanner 
                          onScan={handleQRScan}
                          onClose={() => setShowQRScanner(false)}
                        />
                      )}
                    </div>
                  )}
                  {template.loginConfig.method === 'social' && (
                    <div className="space-y-3">
                      <Label>Proveedores de Login Social</Label>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="facebook-login"
                            checked={template.loginConfig.socialProviders?.includes('facebook')}
                            onChange={(e) => {
                              const providers = template.loginConfig.socialProviders || [];
                              setTemplate(prev => ({
                                ...prev,
                                loginConfig: {
                                  ...prev.loginConfig,
                                  socialProviders: e.target.checked
                                    ? [...providers, 'facebook']
                                    : providers.filter(p => p !== 'facebook')
                                }
                              }));
                            }}
                            className="rounded"
                            data-testid="facebook-provider-checkbox"
                          />
                          <Label htmlFor="facebook-login" className="cursor-pointer">Facebook</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="google-login"
                            checked={template.loginConfig.socialProviders?.includes('google')}
                            onChange={(e) => {
                              const providers = template.loginConfig.socialProviders || [];
                              setTemplate(prev => ({
                                ...prev,
                                loginConfig: {
                                  ...prev.loginConfig,
                                  socialProviders: e.target.checked
                                    ? [...providers, 'google']
                                    : providers.filter(p => p !== 'google')
                                }
                              }));
                            }}
                            className="rounded"
                            data-testid="google-provider-checkbox"
                          />
                          <Label htmlFor="google-login" className="cursor-pointer">Google</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="instagram-login"
                            checked={template.loginConfig.socialProviders?.includes('instagram')}
                            onChange={(e) => {
                              const providers = template.loginConfig.socialProviders || [];
                              setTemplate(prev => ({
                                ...prev,
                                loginConfig: {
                                  ...prev.loginConfig,
                                  socialProviders: e.target.checked
                                    ? [...providers, 'instagram']
                                    : providers.filter(p => p !== 'instagram')
                                }
                              }));
                            }}
                            className="rounded"
                            data-testid="instagram-provider-checkbox"
                          />
                          <Label htmlFor="instagram-login" className="cursor-pointer">Instagram</Label>
                        </div>
                      </div>
                      <Card className="p-4 bg-blue-50 border-blue-200" data-testid="social-login-instructions">
                        <h4 className="font-semibold text-blue-900 mb-2">Configuración en MikroTik</h4>
                        <p className="text-sm text-blue-800 mb-2">
                          Para habilitar login social, configura en MikroTik RouterOS:
                        </p>
                        <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                          <li>IP → Hotspot → Server Profiles</li>
                          <li>Agrega las URLs de autenticación OAuth</li>
                          <li>Configura las API keys de cada proveedor</li>
                          <li>Habilita HTTPS en el servidor hotspot</li>
                        </ol>
                      </Card>
                    </div>
                  )}
                  {template.loginConfig.method === 'form' && (
                    <div className="space-y-3">
                      <Label>Campos del Formulario</Label>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="form-email"
                            checked={template.loginConfig.formFields?.includes('email')}
                            onChange={(e) => {
                              const fields = template.loginConfig.formFields || [];
                              setTemplate(prev => ({
                                ...prev,
                                loginConfig: {
                                  ...prev.loginConfig,
                                  customForm: true,
                                  formFields: e.target.checked
                                    ? [...fields, 'email']
                                    : fields.filter(f => f !== 'email')
                                }
                              }));
                            }}
                            className="rounded"
                            data-testid="form-email-checkbox"
                          />
                          <Label htmlFor="form-email" className="cursor-pointer">Email</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="form-phone"
                            checked={template.loginConfig.formFields?.includes('phone')}
                            onChange={(e) => {
                              const fields = template.loginConfig.formFields || [];
                              setTemplate(prev => ({
                                ...prev,
                                loginConfig: {
                                  ...prev.loginConfig,
                                  customForm: true,
                                  formFields: e.target.checked
                                    ? [...fields, 'phone']
                                    : fields.filter(f => f !== 'phone')
                                }
                              }));
                            }}
                            className="rounded"
                            data-testid="form-phone-checkbox"
                          />
                          <Label htmlFor="form-phone" className="cursor-pointer">Teléfono</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="form-name"
                            checked={template.loginConfig.formFields?.includes('name')}
                            onChange={(e) => {
                              const fields = template.loginConfig.formFields || [];
                              setTemplate(prev => ({
                                ...prev,
                                loginConfig: {
                                  ...prev.loginConfig,
                                  customForm: true,
                                  formFields: e.target.checked
                                    ? [...fields, 'name']
                                    : fields.filter(f => f !== 'name')
                                }
                              }));
                            }}
                            className="rounded"
                            data-testid="form-name-checkbox"
                          />
                          <Label htmlFor="form-name" className="cursor-pointer">Nombre</Label>
                        </div>
                      </div>
                      <Card className="p-4 bg-blue-50 border-blue-200" data-testid="form-login-instructions">
                        <h4 className="font-semibold text-blue-900 mb-2">Configuración en MikroTik</h4>
                        <p className="text-sm text-blue-800">
                          Los datos del formulario se enviarán al servidor MikroTik para validación.
                          Configura la captura de datos en: IP → Hotspot → User Profiles
                        </p>
                      </Card>
                    </div>
                  )}
                  {template.loginConfig.method === 'free' && (
                    <Card className="p-4 bg-green-50 border-green-200" data-testid="free-access-instructions">
                      <h4 className="font-semibold text-green-900 mb-2">✓ Acceso WiFi Gratis</h4>
                      <p className="text-sm text-green-800 mb-3">
                        El usuario podrá conectarse sin autenticación. Configura en MikroTik:
                      </p>
                      <ol className="text-xs text-green-700 space-y-1 list-decimal list-inside">
                        <li>IP → Hotspot → Server Profiles</li>
                        <li>En "Login By": desmarca todas las opciones</li>
                        <li>Habilita "HTTP PAP" para acceso directo</li>
                        <li>Configura límites de velocidad y tiempo en User Profile</li>
                        <li>Opcional: Agrega Terms of Service que el usuario debe aceptar</li>
                      </ol>
                      <p className="text-xs text-green-600 mt-2">
                        💡 Tip: Puedes limitar el ancho de banda o tiempo de conexión para acceso gratuito
                      </p>
                    </Card>
                  )}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Popup de Bienvenida</Label>
                      <Switch
                        checked={template.popup.enabled}
                        onCheckedChange={(checked) => setTemplate(prev => ({
                          ...prev,
                          popup: { ...prev.popup, enabled: checked }
                        }))}
                        data-testid="popup-enabled-switch"
                      />
                    </div>
                    {template.popup.enabled && (
                      <>
                        <Input
                          placeholder="Título del popup"
                          value={template.popup.title}
                          onChange={(e) => setTemplate(prev => ({
                            ...prev,
                            popup: { ...prev.popup, title: e.target.value }
                          }))}
                          data-testid="popup-title-input"
                        />
                        <Textarea
                          placeholder="Mensaje del popup"
                          value={template.popup.message}
                          onChange={(e) => setTemplate(prev => ({
                            ...prev,
                            popup: { ...prev.popup, message: e.target.value }
                          }))}
                          data-testid="popup-message-input"
                        />
                      </>
                    )}
                  </div>
                </TabsContent>

                {/* Design Tab */}
                <TabsContent value="design" className="space-y-4 max-h-[600px] overflow-y-auto pr-2" data-testid="design-content">
                  {/* Logo Section */}
                  <div className="pb-4 border-b">
                    <Label htmlFor="logo" className="flex items-center gap-2 mb-2">
                      <ImageIcon className="w-4 h-4" />
                      Logo de la Empresa
                    </Label>
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <Input
                          id="logo"
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                          data-testid="logo-upload-input"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => document.getElementById('logo').click()}
                          className="w-full"
                          data-testid="logo-upload-btn"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Subir Logo
                        </Button>
                      </div>
                      {template.design.logo ? (
                        <div className="relative border-2 border-dashed rounded-lg p-4 flex items-center justify-center bg-gray-50" data-testid="logo-preview-container">
                          <img src={template.design.logo} alt="Logo" className="h-24 object-contain" data-testid="logo-preview" />
                          <Button
                            variant="destructive"
                            size="sm"
                            className="absolute top-2 right-2"
                            onClick={() => setTemplate(prev => ({ ...prev, design: { ...prev.design, logo: "" } }))}
                            data-testid="logo-remove-btn"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="border-2 border-dashed rounded-lg p-8 text-center text-gray-400" data-testid="logo-placeholder">
                          <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">Sin logo</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Accordion for Color Sections */}
                  <Accordion type="multiple" className="w-full" defaultValue={["general", "login"]} data-testid="design-accordion">
                    {/* Colores Generales */}
                    <AccordionItem value="general">
                      <AccordionTrigger className="text-sm font-semibold">Colores Generales</AccordionTrigger>
                      <AccordionContent className="space-y-3 pt-2">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm">Color Primario</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.primaryColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, primaryColor: e.target.value }}))} className="w-14 h-9 p-1" data-testid="primary-color" />
                              <Input value={template.design.primaryColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, primaryColor: e.target.value }}))} className="flex-1 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm">Color Secundario</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.secondaryColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, secondaryColor: e.target.value }}))} className="w-14 h-9 p-1" data-testid="secondary-color" />
                              <Input value={template.design.secondaryColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, secondaryColor: e.target.value }}))} className="flex-1 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm">Fondo del Cuerpo</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.backgroundColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, backgroundColor: e.target.value }}))} className="w-14 h-9 p-1" data-testid="background-color" />
                              <Input value={template.design.backgroundColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, backgroundColor: e.target.value }}))} className="flex-1 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm">Texto del Cuerpo</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.textColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, textColor: e.target.value }}))} className="w-14 h-9 p-1" data-testid="text-color" />
                              <Input value={template.design.textColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, textColor: e.target.value }}))} className="flex-1 text-sm" />
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Tarjeta de Login */}
                    <AccordionItem value="login">
                      <AccordionTrigger className="text-sm font-semibold">Tarjeta de Login</AccordionTrigger>
                      <AccordionContent className="space-y-3 pt-2">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm">Fondo de Tarjeta</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.loginCardBackground} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, loginCardBackground: e.target.value }}))} className="w-14 h-9 p-1" data-testid="login-card-bg" />
                              <Input value={template.design.loginCardBackground} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, loginCardBackground: e.target.value }}))} className="flex-1 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm">Borde de Tarjeta</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.loginCardBorder} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, loginCardBorder: e.target.value }}))} className="w-14 h-9 p-1" data-testid="login-card-border" />
                              <Input value={template.design.loginCardBorder} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, loginCardBorder: e.target.value }}))} className="flex-1 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm">Texto de Tarjeta</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.loginCardTextColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, loginCardTextColor: e.target.value }}))} className="w-14 h-9 p-1" data-testid="login-card-text" />
                              <Input value={template.design.loginCardTextColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, loginCardTextColor: e.target.value }}))} className="flex-1 text-sm" />
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Inputs/Textbox */}
                    <AccordionItem value="inputs">
                      <AccordionTrigger className="text-sm font-semibold">Campos de Texto (Inputs)</AccordionTrigger>
                      <AccordionContent className="space-y-3 pt-2">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm">Fondo de Input</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.inputBackground} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, inputBackground: e.target.value }}))} className="w-14 h-9 p-1" data-testid="input-bg" />
                              <Input value={template.design.inputBackground} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, inputBackground: e.target.value }}))} className="flex-1 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm">Borde de Input</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.inputBorder} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, inputBorder: e.target.value }}))} className="w-14 h-9 p-1" data-testid="input-border" />
                              <Input value={template.design.inputBorder} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, inputBorder: e.target.value }}))} className="flex-1 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm">Texto de Input</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.inputTextColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, inputTextColor: e.target.value }}))} className="w-14 h-9 p-1" data-testid="input-text" />
                              <Input value={template.design.inputTextColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, inputTextColor: e.target.value }}))} className="flex-1 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm">Placeholder</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.inputPlaceholderColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, inputPlaceholderColor: e.target.value }}))} className="w-14 h-9 p-1" data-testid="input-placeholder" />
                              <Input value={template.design.inputPlaceholderColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, inputPlaceholderColor: e.target.value }}))} className="flex-1 text-sm" />
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Botones */}
                    <AccordionItem value="buttons">
                      <AccordionTrigger className="text-sm font-semibold">Botones</AccordionTrigger>
                      <AccordionContent className="space-y-3 pt-2">
                        <p className="text-xs text-gray-600">Deja vacío para usar el color primario</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm">Fondo de Botón</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.buttonBackground || template.design.primaryColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, buttonBackground: e.target.value }}))} className="w-14 h-9 p-1" data-testid="button-bg" />
                              <Input value={template.design.buttonBackground} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, buttonBackground: e.target.value }}))} placeholder="Usa primario" className="flex-1 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm">Texto de Botón</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.buttonTextColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, buttonTextColor: e.target.value }}))} className="w-14 h-9 p-1" data-testid="button-text" />
                              <Input value={template.design.buttonTextColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, buttonTextColor: e.target.value }}))} className="flex-1 text-sm" />
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Tarjetas de Precios */}
                    <AccordionItem value="pricing">
                      <AccordionTrigger className="text-sm font-semibold">Tarjetas de Precios</AccordionTrigger>
                      <AccordionContent className="space-y-3 pt-2">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm">Fondo</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.pricingCardBackground} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, pricingCardBackground: e.target.value }}))} className="w-14 h-9 p-1" data-testid="pricing-bg" />
                              <Input value={template.design.pricingCardBackground} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, pricingCardBackground: e.target.value }}))} className="flex-1 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm">Borde</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.pricingCardBorder || template.design.secondaryColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, pricingCardBorder: e.target.value }}))} className="w-14 h-9 p-1" data-testid="pricing-border" />
                              <Input value={template.design.pricingCardBorder} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, pricingCardBorder: e.target.value }}))} placeholder="Usa secundario" className="flex-1 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm">Texto</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.pricingCardTextColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, pricingCardTextColor: e.target.value }}))} className="w-14 h-9 p-1" data-testid="pricing-text" />
                              <Input value={template.design.pricingCardTextColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, pricingCardTextColor: e.target.value }}))} className="flex-1 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm">Título</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.pricingCardTitleColor || template.design.primaryColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, pricingCardTitleColor: e.target.value }}))} className="w-14 h-9 p-1" data-testid="pricing-title" />
                              <Input value={template.design.pricingCardTitleColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, pricingCardTitleColor: e.target.value }}))} placeholder="Usa primario" className="flex-1 text-sm" />
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Tarjetas de Ubicación */}
                    <AccordionItem value="locations">
                      <AccordionTrigger className="text-sm font-semibold">Tarjetas de Ubicaciones</AccordionTrigger>
                      <AccordionContent className="space-y-3 pt-2">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm">Fondo</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.locationCardBackground} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, locationCardBackground: e.target.value }}))} className="w-14 h-9 p-1" data-testid="location-bg" />
                              <Input value={template.design.locationCardBackground} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, locationCardBackground: e.target.value }}))} className="flex-1 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm">Borde</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.locationCardBorder} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, locationCardBorder: e.target.value }}))} className="w-14 h-9 p-1" data-testid="location-border" />
                              <Input value={template.design.locationCardBorder} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, locationCardBorder: e.target.value }}))} className="flex-1 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm">Texto</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.locationCardTextColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, locationCardTextColor: e.target.value }}))} className="w-14 h-9 p-1" data-testid="location-text" />
                              <Input value={template.design.locationCardTextColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, locationCardTextColor: e.target.value }}))} className="flex-1 text-sm" />
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {/* Iconos y Enlaces */}
                    <AccordionItem value="icons">
                      <AccordionTrigger className="text-sm font-semibold">Iconos y Enlaces</AccordionTrigger>
                      <AccordionContent className="space-y-3 pt-2">
                        <p className="text-xs text-gray-600">Deja vacío para usar el color primario</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-sm">Color de Iconos</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.iconColor || template.design.primaryColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, iconColor: e.target.value }}))} className="w-14 h-9 p-1" data-testid="icon-color" />
                              <Input value={template.design.iconColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, iconColor: e.target.value }}))} placeholder="Usa primario" className="flex-1 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm">Color de Enlaces</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.linkColor || template.design.primaryColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, linkColor: e.target.value }}))} className="w-14 h-9 p-1" data-testid="link-color" />
                              <Input value={template.design.linkColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, linkColor: e.target.value }}))} placeholder="Usa primario" className="flex-1 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-sm">Fondo Redes Sociales</Label>
                            <div className="flex gap-2 mt-1">
                              <Input type="color" value={template.design.socialIconBackground || template.design.primaryColor} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, socialIconBackground: e.target.value }}))} className="w-14 h-9 p-1" data-testid="social-bg" />
                              <Input value={template.design.socialIconBackground} onChange={(e) => setTemplate(prev => ({ ...prev, design: { ...prev.design, socialIconBackground: e.target.value }}))} placeholder="Usa primario" className="flex-1 text-sm" />
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </TabsContent>

                {/* Pages Tab - MikroTik Configuration */}
                <TabsContent value="pages" className="space-y-4" data-testid="pages-content">
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Configuración de Páginas del Portal</h4>
                      <p className="text-sm text-blue-800">
                        Personaliza los mensajes y comportamiento de las páginas del hotspot MikroTik
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="redirectUrl">URL de Redirección (después del login)</Label>
                      <Input
                        id="redirectUrl"
                        value={template.hotspotPages?.redirectUrl || ""}
                        onChange={(e) => setTemplate(prev => ({
                          ...prev,
                          hotspotPages: { ...prev.hotspotPages, redirectUrl: e.target.value }
                        }))}
                        placeholder="https://www.google.com"
                        data-testid="redirect-url-input"
                      />
                      <p className="text-xs text-gray-600 mt-1">A dónde se redirige el usuario después de iniciar sesión</p>
                    </div>

                    <div className="border-t pt-4">
                      <h4 className="font-semibold mb-3">Página de Estado (Status)</h4>
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="statusTitle">Título</Label>
                          <Input
                            id="statusTitle"
                            value={template.hotspotPages?.statusTitle || ""}
                            onChange={(e) => setTemplate(prev => ({
                              ...prev,
                              hotspotPages: { ...prev.hotspotPages, statusTitle: e.target.value }
                            }))}
                            placeholder="Estado de Conexión"
                            data-testid="status-title-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="statusMessage">Mensaje</Label>
                          <Textarea
                            id="statusMessage"
                            value={template.hotspotPages?.statusMessage || ""}
                            onChange={(e) => setTemplate(prev => ({
                              ...prev,
                              hotspotPages: { ...prev.hotspotPages, statusMessage: e.target.value }
                            }))}
                            placeholder="Estás conectado exitosamente"
                            rows={2}
                            data-testid="status-message-input"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <h4 className="font-semibold mb-3">Página de Cierre de Sesión (Logout)</h4>
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="logoutTitle">Título</Label>
                          <Input
                            id="logoutTitle"
                            value={template.hotspotPages?.logoutTitle || ""}
                            onChange={(e) => setTemplate(prev => ({
                              ...prev,
                              hotspotPages: { ...prev.hotspotPages, logoutTitle: e.target.value }
                            }))}
                            placeholder="Sesión Cerrada"
                            data-testid="logout-title-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="logoutMessage">Mensaje</Label>
                          <Textarea
                            id="logoutMessage"
                            value={template.hotspotPages?.logoutMessage || ""}
                            onChange={(e) => setTemplate(prev => ({
                              ...prev,
                              hotspotPages: { ...prev.hotspotPages, logoutMessage: e.target.value }
                            }))}
                            placeholder="Has cerrado sesión correctamente"
                            rows={2}
                            data-testid="logout-message-input"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <h4 className="font-semibold mb-3">Página de Error</h4>
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="errorTitle">Título</Label>
                          <Input
                            id="errorTitle"
                            value={template.hotspotPages?.errorTitle || ""}
                            onChange={(e) => setTemplate(prev => ({
                              ...prev,
                              hotspotPages: { ...prev.hotspotPages, errorTitle: e.target.value }
                            }))}
                            placeholder="Error de Conexión"
                            data-testid="error-title-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="errorMessage">Mensaje</Label>
                          <Textarea
                            id="errorMessage"
                            value={template.hotspotPages?.errorMessage || ""}
                            onChange={(e) => setTemplate(prev => ({
                              ...prev,
                              hotspotPages: { ...prev.hotspotPages, errorMessage: e.target.value }
                            }))}
                            placeholder="No se pudo conectar. Verifica tus credenciales."
                            rows={2}
                            data-testid="error-message-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* SSL Tab */}
                <TabsContent value="ssl" className="space-y-4" data-testid="ssl-content">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Habilitar SSL</Label>
                      <p className="text-sm text-gray-600">Configura certificados SSL para conexiones seguras</p>
                    </div>
                    <Switch
                      checked={template.sslConfig.enabled}
                      onCheckedChange={(checked) => setTemplate(prev => ({
                        ...prev,
                        sslConfig: { ...prev.sslConfig, enabled: checked }
                      }))}
                      data-testid="ssl-enabled-switch"
                    />
                  </div>
                  {template.sslConfig.enabled && (
                    <>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="ssl-generic-option">
                        <Label className="text-blue-900 mb-2 block">¿No tienes certificado SSL?</Label>
                        <p className="text-sm text-blue-700 mb-3">
                          Genera un certificado SSL genérico autofirmado para pruebas
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={generateGenericSSL}
                          className="w-full border-blue-300 hover:bg-blue-100"
                          data-testid="ssl-generate-generic-btn"
                        >
                          <Lock className="w-4 h-4 mr-2" />
                          Generar SSL Genérico
                        </Button>
                      </div>
                      <div>
                        <Label htmlFor="certificate">Certificado SSL</Label>
                        <Textarea
                          id="certificate"
                          placeholder="Pega aquí tu certificado SSL o genera uno genérico"
                          value={template.sslConfig.certificate}
                          onChange={(e) => setTemplate(prev => ({
                            ...prev,
                            sslConfig: { ...prev.sslConfig, certificate: e.target.value }
                          }))}
                          rows={6}
                          data-testid="ssl-certificate-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="privateKey">Clave Privada</Label>
                        <Textarea
                          id="privateKey"
                          placeholder="Pega aquí tu clave privada o genera una genérica"
                          value={template.sslConfig.privateKey}
                          onChange={(e) => setTemplate(prev => ({
                            ...prev,
                            sslConfig: { ...prev.sslConfig, privateKey: e.target.value }
                          }))}
                          rows={6}
                          data-testid="ssl-privatekey-input"
                        />
                      </div>
                    </>
                  )}
                </TabsContent>

                {/* Locations Tab */}
                <TabsContent value="locations" className="space-y-4" data-testid="locations-content">
                  <div className="flex items-center justify-between">
                    <Label>Ubicaciones y Puntos de Venta</Label>
                    <Button onClick={addLocation} size="sm" data-testid="add-location-btn">
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar
                    </Button>
                  </div>
                  {template.locations.map((location, index) => (
                    <Card key={index} className="p-4" data-testid={`location-${index}`}>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label>Ubicación {index + 1}</Label>
                          <Button
                            onClick={() => removeLocation(index)}
                            size="sm"
                            variant="destructive"
                            data-testid={`remove-location-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <Input
                          placeholder="Nombre"
                          value={location.name}
                          onChange={(e) => updateLocation(index, 'name', e.target.value)}
                          data-testid={`location-name-${index}`}
                        />
                        <Input
                          placeholder="Dirección"
                          value={location.address}
                          onChange={(e) => updateLocation(index, 'address', e.target.value)}
                          data-testid={`location-address-${index}`}
                        />
                        <Input
                          placeholder="Teléfono"
                          value={location.phone}
                          onChange={(e) => updateLocation(index, 'phone', e.target.value)}
                          data-testid={`location-phone-${index}`}
                        />
                      </div>
                    </Card>
                  ))}
                </TabsContent>

                {/* Pricing Tab */}
                <TabsContent value="pricing" className="space-y-4" data-testid="pricing-content">
                  <div className="flex items-center justify-between">
                    <Label>Planes de Precios</Label>
                    <Button onClick={addPricing} size="sm" data-testid="add-pricing-btn">
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar
                    </Button>
                  </div>
                  {template.pricing.map((price, index) => (
                    <Card key={index} className="p-4" data-testid={`pricing-${index}`}>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Label>Plan {index + 1}</Label>
                          <Button
                            onClick={() => removePricing(index)}
                            size="sm"
                            variant="destructive"
                            data-testid={`remove-pricing-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <Input
                          placeholder="Nombre del plan"
                          value={price.name}
                          onChange={(e) => updatePricing(index, 'name', e.target.value)}
                          data-testid={`pricing-name-${index}`}
                        />
                        <Input
                          placeholder="Duración (ej: 1h, 24h)"
                          value={price.duration}
                          onChange={(e) => updatePricing(index, 'duration', e.target.value)}
                          data-testid={`pricing-duration-${index}`}
                        />
                        <Input
                          placeholder="Precio (ej: $5.00)"
                          value={price.price}
                          onChange={(e) => updatePricing(index, 'price', e.target.value)}
                          data-testid={`pricing-price-${index}`}
                        />
                        <Input
                          placeholder="Velocidad (ej: 10 Mbps)"
                          value={price.speed}
                          onChange={(e) => updatePricing(index, 'speed', e.target.value)}
                          data-testid={`pricing-speed-${index}`}
                        />
                      </div>
                    </Card>
                  ))}
                </TabsContent>

                {/* Social Media Tab */}
                <TabsContent value="social" className="space-y-4" data-testid="social-content">
                  <Label>Redes Sociales</Label>
                  <Input
                    placeholder="Facebook URL"
                    value={template.socialMedia.facebook}
                    onChange={(e) => setTemplate(prev => ({
                      ...prev,
                      socialMedia: { ...prev.socialMedia, facebook: e.target.value }
                    }))}
                    data-testid="facebook-input"
                  />
                  <Input
                    placeholder="Instagram URL"
                    value={template.socialMedia.instagram}
                    onChange={(e) => setTemplate(prev => ({
                      ...prev,
                      socialMedia: { ...prev.socialMedia, instagram: e.target.value }
                    }))}
                    data-testid="instagram-input"
                  />
                  <Input
                    placeholder="Twitter/X URL"
                    value={template.socialMedia.twitter}
                    onChange={(e) => setTemplate(prev => ({
                      ...prev,
                      socialMedia: { ...prev.socialMedia, twitter: e.target.value }
                    }))}
                    data-testid="twitter-input"
                  />
                  <Input
                    placeholder="WhatsApp (número con código de país)"
                    value={template.socialMedia.whatsapp}
                    onChange={(e) => setTemplate(prev => ({
                      ...prev,
                      socialMedia: { ...prev.socialMedia, whatsapp: e.target.value }
                    }))}
                    data-testid="whatsapp-input"
                  />
                </TabsContent>

                {/* Terms Tab */}
                <TabsContent value="terms" className="space-y-4" data-testid="terms-content">
                  <Label>Términos y Condiciones</Label>
                  <Textarea
                    placeholder="Escribe aquí los términos y condiciones..."
                    value={template.termsAndConditions}
                    onChange={(e) => setTemplate(prev => ({ ...prev, termsAndConditions: e.target.value }))}
                    rows={12}
                    data-testid="terms-input"
                  />
                </TabsContent>

                {/* Preview Tab (Mobile Only) */}
                <TabsContent value="preview" className="lg:hidden" data-testid="preview-content-mobile">
                  <HotspotPreview template={template} />
                </TabsContent>
              </Tabs>
            </Card>
          </div>

          {/* Preview Panel (Desktop) */}
          <div className="hidden lg:block sticky top-24 h-fit" data-testid="preview-panel-desktop">
            <Card className="p-6" style={{backgroundColor: '#ffffff', color: '#1f2937'}}>
              <div className="flex items-center gap-2 mb-4">
                <Eye className="w-5 h-5 text-gray-600" />
                <h2 className="text-lg font-semibold">Vista Previa</h2>
              </div>
              <HotspotPreview template={template} />
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalDesignerEditor;
